/**
 * Semantic equality for serialized TipTap doc bodies.
 *
 * Lives in `src/editor/` rather than a feature folder because
 * `<MarkdownEditor>` itself depends on it: the value-sync effect must not
 * replace a live document over a difference that carries no user intent
 * (doing so dumps the caret at the end of the doc — see the guard there).
 * Every rich-text surface therefore shares one definition of "the same
 * body", instead of the wiki holding the only correct one.
 *
 * A body gets serialized two different ways depending on who produced the
 * string:
 *   - The live editor emits `JSON.stringify(editor.getJSON())` — TipTap's
 *     own attribute-key ordering, with every `paragraph` carrying an `id`
 *     the ParagraphIdExtension back-fills on mount (a fresh
 *     `crypto.randomUUID()` minted with ZERO user intent).
 *   - The server emits `JSON.stringify(body.body)` — the parsed-from-DB
 *     key ordering, and for a doc that pre-dates the paragraph-id schema,
 *     no `id` attrs at all.
 *
 * The server ordering is not incidental: wiki bodies live in a `jsonb`
 * column, and `jsonb` does not store keys as written — it canonicalizes
 * them by length-then-bytewise. TipTap emits `{"type":…,"text":…}`;
 * Postgres hands back `{"text":…,"type":…}`. Both keys are 4 characters
 * and `"text" < "type"`, so EVERY text node comes back reordered. A body
 * that round-trips through a save is therefore guaranteed to byte-differ
 * from the one the editor still holds, with zero user intent.
 *
 * The two byte-differ on any content-bearing doc even when the user typed
 * nothing. A byte-level `===` comparison therefore reports a phantom change
 * — the failure mode behind the recovery banner appearing after a
 * zero-change edit session, and behind the autosave debounce writing a
 * stale IndexedDB row for content nobody touched.
 *
 * These helpers compare bodies by MEANING, not bytes:
 *   - key ordering is normalized (a stable, recursively key-sorted form),
 *   - volatile attrs are dropped (paragraph `id` back-fill churn is not
 *     user content — a passage with a different anchor id is the same
 *     passage; likewise a `workItemEmbed`'s null `kind` hint, which
 *     TipTap default-back-fills onto chips authored before the attr
 *     existed),
 *   - in-flight upload placeholder nodes are dropped (a legacy IndexedDB
 *     row written before the flush-time strip existed may still carry
 *     them; a dead skeleton is not user content and must not surface a
 *     recovery banner),
 *   - the several serialized forms of "empty doc" collapse to equal.
 *
 * Genuine content differences (text, marks, structure, non-paragraph
 * attrs) survive normalization and still compare unequal — a real
 * mid-edit crash still surfaces the recovery banner, and a genuine
 * remote edit still reaches the editor.
 */

import type { TipTapJsonContent } from '@/api/types-bridge'
import { stripInFlightPlaceholderNodes } from './strip-inflight-placeholders'

function parseDocJson(text: string): unknown {
  if (!text) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return null
  }
}

/**
 * "Empty doc" in every serialized form the editor + server can produce:
 * the empty string, the `'{}'` brand-new-doc default, an explicit
 * `{ content: [] }`, and a mounted editor's normalized single-empty-
 * paragraph doc (`{"type":"doc","content":[{"type":"paragraph"}]}`).
 */
export function isEmptyDocString(text: string): boolean {
  if (text === '' || text === '{}') return true
  const parsed = parseDocJson(text) as { type?: string; content?: unknown[] } | null
  if (!parsed || typeof parsed !== 'object') return false
  if (parsed.content === undefined) return Object.keys(parsed).length === 0
  if (!Array.isArray(parsed.content)) return false
  if (parsed.content.length === 0) return true
  if (parsed.content.length !== 1) return false
  const only = parsed.content[0] as { type?: string; content?: unknown[] } | undefined
  return only?.type === 'paragraph' && (only.content === undefined || only.content.length === 0)
}

/**
 * Per-node-type attrs that carry no user intent and must not read as a
 * content change.
 *
 * - `paragraph.id` — back-fill id churn (and differing ids between a
 *   local draft and the server copy) is not user content.
 * - `workItemEmbed.kind` WHEN NULL — the kind attr is a BE lookup hint
 *   added after the node type shipped. TipTap default-back-fills it as
 *   `kind: null` on every pre-existing chip at `getJSON()` time, while
 *   the stored remote body has no such key at all. Left in, every
 *   legacy doc containing a work-item chip compares unequal on mount:
 *   `savingState` flips to `'local-dirty'` ("Draft on this device" with
 *   nothing typed), a blur-triggered autosave bumps
 *   `wiki_nodes.updated_at`, and a spurious recovery banner can appear
 *   — exactly what this module exists to prevent. A REAL hint
 *   (`'task'`, …) is kept: it's stable across serializations, so it
 *   compares equal on both sides anyway.
 *
 * A node whose only attr was volatile loses its `attrs` key entirely, so
 * it canonicalizes identically to one that never carried it.
 */
function isVolatileAttr(nodeType: unknown, attrKey: string, attrValue: unknown): boolean {
  if (nodeType === 'paragraph' && attrKey === 'id') return true
  if (nodeType === 'workItemEmbed' && attrKey === 'kind' && attrValue === null) return true
  // Same reason as `kind`: TipTap back-fills a newly-added attribute's
  // default onto every pre-existing node at getJSON() time, so a chip
  // stored before `shorthand` existed serializes with `shorthand: null`
  // and would report a phantom change on mount for every doc holding one.
  if (nodeType === 'workItemEmbed' && attrKey === 'shorthand' && attrValue === null) return true
  return false
}

function stripVolatile(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripVolatile)
  if (!value || typeof value !== 'object') return value
  const obj = value as Record<string, unknown>
  const result: Record<string, unknown> = {}
  for (const key of Object.keys(obj)) {
    const child = obj[key]
    if (key === 'attrs' && child && typeof child === 'object' && !Array.isArray(child)) {
      const source = child as Record<string, unknown>
      const attrs: Record<string, unknown> = {}
      for (const attrKey of Object.keys(source)) {
        if (isVolatileAttr(obj.type, attrKey, source[attrKey])) continue
        attrs[attrKey] = stripVolatile(source[attrKey])
      }
      if (Object.keys(attrs).length > 0) result[key] = attrs
      continue
    }
    result[key] = stripVolatile(child)
  }
  return result
}

/** Key-order-independent serialization used as the comparison canonical form. */
function canonicalize(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (Array.isArray(value)) return `[${value.map(canonicalize).join(',')}]`
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    const keys = Object.keys(obj).sort()
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalize(obj[k])}`).join(',')}}`
  }
  return JSON.stringify(value)
}

/**
 * SEMANTIC equality of two serialized doc bodies. Returns `true` when the
 * bodies mean the same thing — ignoring attribute-key ordering, volatile
 * paragraph ids, and the several encodings of "empty". Falls back to
 * byte equality on a parse failure (either side non-JSON) so a legacy
 * markdown row is never falsely claimed equal to a JSON one.
 */
export function docBodiesEqual(a: string, b: string): boolean {
  if (a === b) return true
  if (isEmptyDocString(a) && isEmptyDocString(b)) return true
  const parsedA = parseDocJson(a)
  const parsedB = parseDocJson(b)
  if (parsedA == null || parsedB == null) return false
  // A bare JSON scalar is not a ProseMirror document. Without this, the
  // placeholder strip's object spread turns `123` and `456` alike into
  // `{}` and they canonicalize equal — two different bodies reported as
  // the same. Only reachable for a legacy row whose entire body is a
  // scalar, but the whole point of this module is that its callers can
  // trust an `equal` answer.
  if (typeof parsedA !== 'object' || typeof parsedB !== 'object') return false
  const strippedA = stripInFlightPlaceholderNodes(parsedA as TipTapJsonContent)
  const strippedB = stripInFlightPlaceholderNodes(parsedB as TipTapJsonContent)
  return canonicalize(stripVolatile(strippedA)) === canonicalize(stripVolatile(strippedB))
}
